import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMap } from '@vis.gl/react-maplibre'
import {
  fitMapToCommunityMapLayerBounds,
  resolveCommunityMapLayerFeatures,
  resolveMapLibreMapInstance,
} from '../../utils/community-map-layer-utils'

const FIT_RETRY_DELAYS_MS = [0, 200, 600, 1200, 2500]

function buildFocusedLayerKey(layersList) {
  return (layersList ?? [])
    .filter((layer) => layer.isFocused)
    .map((layer) => `${layer.id}:${layer.focusNonce ?? 0}:${layer.isLoaded ? 1 : 0}`)
    .join('|')
}

export function CommunityMapLayerFocusHandler({ layersList, focusMapLayerRef, mapRef }) {
  const { current: mapFromContext } = useMap()
  const layersListRef = useRef(layersList)
  layersListRef.current = layersList

  const resolveMapInstance = useCallback(() => {
    const mapRefInstance = resolveMapLibreMapInstance(mapRef?.current)
    if (mapRefInstance) return mapRefInstance
    return resolveMapLibreMapInstance(mapFromContext)
  }, [mapFromContext, mapRef])

  const tryFitLayer = useCallback((layerId) => {
    const map = resolveMapInstance()
    if (!map) return false

    const layer = layersListRef.current.find(
      (item) => Number(item.id) === Number(layerId),
    )
    if (!layer) return false

    const features = resolveCommunityMapLayerFeatures(layer, map)
    if (features.length === 0) return false

    return fitMapToCommunityMapLayerBounds(map, features)
  }, [resolveMapInstance])

  useEffect(() => {
    if (!focusMapLayerRef) return undefined
    focusMapLayerRef.current = tryFitLayer
    return () => {
      focusMapLayerRef.current = null
    }
  }, [focusMapLayerRef, tryFitLayer])

  const focusedLayerKey = useMemo(
    () => buildFocusedLayerKey(layersList),
    [layersList],
  )

  useEffect(() => {
    if (!focusedLayerKey || !resolveMapInstance()) return undefined

    const focusedLayer = (layersList ?? []).find((layer) => layer.isFocused)
    if (!focusedLayer) return undefined

    const timeouts = FIT_RETRY_DELAYS_MS.map((delay) => (
      setTimeout(() => {
        tryFitLayer(focusedLayer.id)
      }, delay)
    ))

    return () => {
      timeouts.forEach(clearTimeout)
    }
  }, [focusedLayerKey, layersList, resolveMapInstance, tryFitLayer])

  return null
}
